import { Controller } from "@hotwired/stimulus";

/**
 * Featured Gallery Controller
 *
 * Horizontal scrolling gallery for featured models with:
 * - Smooth scroll navigation via arrow buttons
 * - Progress dots tracking scroll position
 * - Touch/drag scrolling support
 * - Keyboard navigation
 */
export default class extends Controller<HTMLElement> {
  static targets = ["track", "prevBtn", "nextBtn", "dots"];

  declare readonly trackTarget: HTMLElement;
  declare readonly prevBtnTarget: HTMLButtonElement;
  declare readonly nextBtnTarget: HTMLButtonElement;
  declare readonly dotsTarget: HTMLElement;

  private cards: HTMLElement[] = [];
  private dotElements: HTMLElement[] = [];
  private currentIndex: number = 0;
  private resizeObserver: ResizeObserver | null = null;

  connect(): void {
    this.cards = Array.from(
      this.trackTarget.querySelectorAll(".featured-gallery-card")
    );
    this.dotElements = Array.from(
      this.dotsTarget.querySelectorAll(".featured-gallery-dot")
    );

    this.setupScrollListener();
    this.setupResizeObserver();
    this.updateNavState();
    this.updateDots();
  }

  disconnect(): void {
    this.resizeObserver?.disconnect();
  }

  scrollLeft(): void {
    const scrollAmount = this.getScrollAmount();
    this.trackTarget.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  }

  scrollRight(): void {
    const scrollAmount = this.getScrollAmount();
    this.trackTarget.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }

  private getScrollAmount(): number {
    // Scroll roughly one card width plus gap
    const firstCard = this.cards[0];
    if (!firstCard) return 300;
    return firstCard.offsetWidth + 16; // 16px = gap-4
  }

  private setupScrollListener(): void {
    let scrollTimeout: ReturnType<typeof setTimeout>;

    this.trackTarget.addEventListener(
      "scroll",
      () => {
        // Throttle scroll updates
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updateNavState();
          this.updateDots();
        }, 50);
      },
      { passive: true }
    );
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateNavState();
      this.updateDots();
    });
    this.resizeObserver.observe(this.trackTarget);
  }

  private updateNavState(): void {
    const { scrollLeft, scrollWidth, clientWidth } = this.trackTarget;
    const isAtStart = scrollLeft <= 10;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;

    this.prevBtnTarget.disabled = isAtStart;
    this.nextBtnTarget.disabled = isAtEnd;
  }

  private updateDots(): void {
    if (this.cards.length === 0 || this.dotElements.length === 0) return;

    const { scrollLeft, clientWidth } = this.trackTarget;
    const centerPoint = scrollLeft + clientWidth / 2;

    // Find which card is most centered
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(centerPoint - cardCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;

      // Update dot active states
      this.dotElements.forEach((dot, index) => {
        if (index === closestIndex) {
          dot.classList.add("active");
        } else {
          dot.classList.remove("active");
        }
      });
    }
  }
}
